import React from 'react';
import { Card, Badge, Row, Col } from 'react-bootstrap';
import './DetailSection.css';

export interface DetailField {
  label: string;
  value: string | React.ReactNode;
  colSpan?: 6 | 12; // Default: 6 (half width)
}

export interface DetailSectionProps {
  title: string;
  status?: {
    label: string;
    variant: 'success' | 'danger' | 'warning' | 'secondary';
  };
  fields: DetailField[];
  actions?: React.ReactNode; // Edit, Delete, Kill buttons, etc.
  conditions?: {
    title: string;
    items: Array<{
      name: string;
      value: string | React.ReactNode;
    }>;
  };
  className?: string;
}

/**
 * Reusable Detail Section Component
 * Displays details in a card format with header, status badge, actions, and fields
 * Used across Match Detail, Tournament Detail, Feature Detail, etc.
 */
export const DetailSection: React.FC<DetailSectionProps> = ({
  title,
  status,
  fields,
  actions,
  conditions,
  className
}) => {
  return (
    <Card className={className || 'mb-4 detail-section-card'}>
      <Card.Header
        className="d-flex justify-content-between align-items-center border-0 bg-white detail-section-header"
      >
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0 detail-section-title">{title}</h5>
          {status && (
            status.variant === 'success' ? (
              <span className="detail-section-status-pill-success">
                {status.label}
              </span>
            ) : status.variant === 'danger' ? (
              <span className="detail-section-status-pill-killed">
                {status.label}
              </span>
            ) : (
              <Badge bg={status.variant} style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                {status.label}
              </Badge>
            )
          )}
        </div>
        {actions && (
          <div className="d-flex gap-2">
            {actions}
          </div>
        )}
      </Card.Header>
      <Card.Body className="detail-section-body">
        <Row className="g-3">
          {fields.map((field, index) => (
            <Col md={field.colSpan || 6} key={index}>
              <div>
                <span className="detail-section-field-label">
                  {field.label}
                </span>
                <div className="detail-section-field-value">
                  {field.value || '-'}
                </div>
              </div>
            </Col>
          ))}
        </Row>
        {conditions && conditions.items.length > 0 && (
          <div className="mt-4 pt-3 detail-section-conditions">
            <h6 className="detail-section-conditions-title">{conditions.title}</h6>

            {/* Header row: Condition Name / Value */}
            <Row className="g-3 mb-1">
              <Col md={6}>
                <span className="detail-section-conditions-label">
                  Condition Name
                </span>
              </Col>
              <Col md={6}>
                <span className="detail-section-conditions-label">
                  Value
                </span>
              </Col>
            </Row>

            {/* Condition rows */}
            {conditions.items.map((item, index) => (
              <Row className="g-3" key={index}>
                <Col md={6}>
                  <div className="detail-section-conditions-value">
                    {item.name}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="detail-section-conditions-value">
                    {item.value || '-'}
                  </div>
                </Col>
              </Row>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DetailSection;
